import { useApiQuery } from "@gladis/api-client";
import { formatEur, type PropertyListDto } from "@gladis/shared";
import { EmptyState, ErrorState, LoadingState, Table } from "@gladis/ui";
import { useT } from "@gladis/ui/i18n";
import type { ReactNode } from "react";

/**
 * The prefix EVERY properties mutation invalidates.
 *
 * Not the list key alone. The list, a property's detail and its valuation history are queries over
 * one set of facts, and a mutation changes more than the one a component happens to own: adding a
 * valuation changes the property's current value on the LIST as well as its history on the detail
 * page. Invalidating narrowly is how savings' chart sat on its empty state while two accounts were
 * plainly listed underneath it.
 *
 * TanStack matches query keys by prefix, so this one covers all of them.
 */
export const PROPERTIES_KEY = ["properties"] as const;

/** The list query itself. Read by the page and by the archived disclosure. */
export const PROPERTIES_LIST_KEY = ["properties", "list"] as const;

export function useProperties(): ReturnType<typeof useApiQuery<PropertyListDto>> {
  return useApiQuery<PropertyListDto>(PROPERTIES_LIST_KEY, "/properties");
}

/** Street, number and addition — the address as a household says it aloud. */
function addressLine(address: PropertyListDto["active"][number]["address"]): string {
  return `${address.street} ${address.houseNumber}${address.houseAddition ?? ""}`.trim();
}

/**
 * The household's properties: one row per active property.
 *
 * Co-located in the properties package rather than in `apps/app`, because it knows what a property
 * is. `apps/app` composes it; it does not own it.
 *
 * EVERY FIGURE GOES THROUGH `formatEur`. Not a style preference — there is a test in
 * `packages/shared` that greps the repository for a second currency formatter and fails if one
 * appears, because "€ 1.234,00" on one screen and "€1,234.00" on another is the kind of bug nobody
 * files and everybody notices.
 *
 * NO FIGURE IS COMPUTED HERE. `currentValue`, `totalOwed` and `equity` all arrive derived from the
 * server, against a single evaluation date it also returns. A component that did its own arithmetic
 * would be a second implementation of a rule that must agree with the totals — and it would do it in
 * floating point, which is the one carrier this feature's arithmetic exists to avoid.
 *
 * NO ADD CONTROL IN HERE, and that is the page's decision made once rather than this component's twice.
 * "Add a property" is the page's primary action and lives at the end of its title row, where `Page.Title`
 * puts every page's. It used to appear twice in this file — once above the table, once in the empty
 * state — so the same act had two positions depending on how much data the household had, and neither
 * was the one the Cards page had already established.
 */
export function PropertyList({ onOpen }: { onOpen: (propertyId: string) => void }): ReactNode {
  const t = useT();
  const properties = useProperties();

  if (properties.isPending) return <LoadingState>{t("prop.list.loading")}</LoadingState>;
  if (properties.isError) return <ErrorState>{t("prop.list.loadFailed")}</ErrorState>;

  const data = properties.data;
  if (!data) return <ErrorState>{t("prop.list.loadFailed")}</ErrorState>;

  if (data.active.length === 0) {
    return <EmptyState>{t("prop.list.empty")}</EmptyState>;
  }

  return (
    <div className="flex flex-col gap-4">
      <Table>
        <Table.Head>
          <Table.Row>
            <Table.HeaderCell>{t("prop.list.colProperty")}</Table.HeaderCell>
            <Table.HeaderCell numeric>{t("prop.list.colValue")}</Table.HeaderCell>
            <Table.HeaderCell numeric>{t("prop.list.colOwed")}</Table.HeaderCell>
            <Table.HeaderCell numeric>{t("prop.list.colEquity")}</Table.HeaderCell>
            <Table.HeaderCell numeric>{t("prop.list.colGain")}</Table.HeaderCell>
          </Table.Row>
        </Table.Head>
        <Table.Body>
          {data.active.map((property) => (
            <Table.Row key={property.id}>
              <Table.Cell>
                <button
                  type="button"
                  className="flex flex-col text-left"
                  onClick={() => {
                    onOpen(property.id);
                  }}
                >
                  <span className="font-medium">{addressLine(property.address)}</span>
                  <span className="text-sm text-muted">{property.address.city}</span>
                </button>
              </Table.Cell>
              <Table.Cell numeric>{formatEur(property.currentValue)}</Table.Cell>
              <Table.Cell numeric>{formatEur(property.totalOwed)}</Table.Cell>
              {/*
                Negative equity is shown as a negative number, not clamped or hidden. An underwater
                mortgage is a real situation and FR-036 requires it to read as one.
              */}
              <Table.Cell numeric>{formatEur(property.equity)}</Table.Cell>
              <Table.Cell numeric>
                {formatEur(property.gain)} ({property.gainPct.toFixed(1)}%)
              </Table.Cell>
            </Table.Row>
          ))}
        </Table.Body>
      </Table>
    </div>
  );
}

/**
 * What the household owns, owes and is left with — the three figures the page opens on.
 *
 * ==========================================================================================
 * ITS OWN COMPONENT SO IT CAN SIT ABOVE THE CHART
 * ==========================================================================================
 *
 * These lived inside `PropertyList`, which put them below the chart once the chart moved to the top
 * of the page. That is the wrong order: a household asks "what are we worth" first, "how has it
 * moved" second, and "which house is which" third. A summary underneath the graph it summarises is
 * a summary nobody reads.
 *
 * It reads the same query key as the list, so this is one request rather than two — the second
 * caller is served from the cache.
 *
 * NO FIGURE IS COMPUTED HERE. All three arrive derived from the server, summed from the same
 * already-rounded per-property figures the rows show. Adding the column up by hand must give this
 * number (NFR-003), which is only guaranteed while nothing here recomputes it.
 */
export function PropertyTotals(): ReactNode {
  const t = useT();
  const properties = useProperties();
  const data = properties.data;
  if (!data) return null;

  return (
    <dl className="grid grid-cols-1 gap-4 sm:grid-cols-3">
      <Total label={t("prop.list.colValue")} amount={data.totals.currentValue} />
      <Total label={t("prop.list.colOwed")} amount={data.totals.totalOwed} />
      <Total label={t("prop.list.colEquity")} amount={data.totals.equity} />
    </dl>
  );
}

function Total({ label, amount }: { label: string; amount: number }): ReactNode {
  return (
    <div className="rounded-control border-line bg-panel border p-4">
      <dt className="text-muted text-sm">{label}</dt>
      <dd className="text-2xl font-semibold tabular-nums">{formatEur(amount)}</dd>
    </div>
  );
}
